import { TextField, createTheme, ThemeProvider, styled } from "@mui/material";
import { useMachine } from "@xstate/react";
import React, { useState, useEffect, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { createMachine } from "xstate";
import FormContext from "../../contexts/FormContext";
import { sendLoadingPostRequest } from "../../request/request";
import { IAddressModel } from "../../types/IAddressModel";
import { IIdentificationModel } from "../../types/IIdentificationModel";
import { LoadingPanel } from "../LoadingPanel/LoadingPanel";
import { Row } from "./Row";
import { Table } from "./Table";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { IBeneficiaryItem } from "../../types/IBeneficiaryItem";
import { SurveyListType } from "../../types/SurveyListType";
import { PPPType } from "../../types/PPPType";

interface IStep3Form {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

interface IRenderTextProps {
  children: React.ReactNode;
}

interface RenderIdentity {
  identification: IIdentificationModel;
}

interface RenderAddress {
  address: IAddressModel;
}

interface IConfirmPhone {
  code: string;
}

interface RenderBeneficiary {
  beneficiary: IBeneficiaryItem;
}

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
});

const toggleMachine = createMachine({
  id: "toggle",
  initial: "initial",
  states: {
    initial: {
      on: { TOGGLE: "confirmPhone" },
    },
    confirmPhone: {
      on: { TOGGLE: "initial" },
    },
  },
});

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 40,
  },
}));

const showQuestion: number[] = [8, 9];

const Step3Form = ({ setStep }: IStep3Form) => {
  const { step1, step2, ppp, setPPP, setNumber } = useContext(FormContext);

  const { height, width } = useWindowDimensions();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
    clearErrors,
    setError,
  } = useForm<IConfirmPhone>();
  const [state, send] = useMachine(toggleMachine);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<boolean>(false);
  const [dataCheck, setDataCheck] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(step2?.counter ?? 0);

  const sendPPPData = async () => {
    const localData = {
      survey: ppp?.survey,
      surveyHighRisk: ppp?.surveyHighRisk,
      isHighRisk: ppp?.isHighRisk,
      withBeneficiary: ppp?.withBeneficiary,
      firstBeneficiary: ppp?.firstBeneficiary,
      secondBeneficiary: ppp?.secondBeneficiary,
      thirdBeneficiary: ppp?.thirdBeneficiary,
    };

    const {} = await sendLoadingPostRequest<PPPType, boolean>({
      loadingFunction: setLoading,
      endpoint: "Agreement/PPP",
      params: {
        surveyList: localData,
        profession: step2?.profession,
      },
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    sendPPPData();
  }, []);

  useEffect(() => {
    if (dataCheck) {
      setShowError(false);
    }
  }, [dataCheck]);

  useEffect(() => {
    if (step2 != null) {
      step2.counter = counter;
    }
  }, [counter]);

  const isEmpty = (text?: string) => {
    if (text == null || text === "") return true;
    return false;
  };

  const confirmPhone = async (data: IConfirmPhone) => {
    if (dataCheck) {
      if (state.value === "confirmPhone") {
        const { code } = getValues();
        const { data: confirmResponse, success } = await sendLoadingPostRequest<
          IConfirmPhone,
          string
        >({
          loadingFunction: setLoading,
          endpoint: "Agreement/VerifyAndSend",
          params: {
            code,
          },
        });

        if (success) {
          if (confirmResponse?.data && confirmResponse?.data !== "Błędny kod") {
            if (setNumber != null) {
              setNumber(confirmResponse?.data ?? "");
            }
            setStep(6);
          } else if (!confirmResponse?.data) {
            setStep(-2);
          } else {
            setErrorCode(true);
          }
        } else {
          setErrorCode(true);
        }
      } else {
        var response = await sendCode();
        if (response) {
          send({ type: "TOGGLE" });
        }
      }
    } else {
      setShowError(true);
    }
  };

  const sendCode = async () => {
    setStep(5);
    setCounter((prev) => prev + 1);
    const { success } = await sendLoadingPostRequest<never, boolean>({
      loadingFunction: setLoading,
      endpoint: "Agreement/SendSms",
    });

    return success;
  };

  const getBase64 = async (file: any): Promise<string> => {
    if (file && file.length > 0) {
      var reader = new FileReader();
      reader.readAsDataURL(file[0]);
      return new Promise((resolve) => {
        reader.onload = () => {
          resolve((reader.result as string) ?? "");
        };
      });
    }
    return "";
  };

  const downloadPDF = async (pdf: any, name: string) => {
    const file = pdf[0];
    const { name: fileName } = file;

    const mimeType = fileName.substr(fileName.lastIndexOf(".") + 1);

    const downloadLink = document.createElement("a");
    const baseFile = await getBase64(pdf);
    if (baseFile) {
      if (mimeType) {
        const fileName = `${name}.${mimeType}`;

        downloadLink.href = baseFile;
        downloadLink.download = fileName;
        downloadLink.click();
      }
    }
  };

  const clearPPP = async () => {
    if (setPPP != null) {
      const { data: response, success } = await sendLoadingPostRequest<
        never,
        SurveyListType
      >({
        loadingFunction: setLoading,
        endpoint: "agreement/GetPPP",
      });
      if (success && response?.data) {
        const resp = response?.data;
        if (resp == null) return;

        setPPP((prev) =>
          prev != null
            ? {
                ...prev,
                surveyHighRisk: resp.surveyHighRisk,
              }
            : undefined
        );
      }
    }
  };

  const encodeWhiteSpaces = (str: string | undefined) => {
    if (isEmpty(str)) return "";
    return str
      ?.split("")
      .map(function (c) {
        return c === " " ? "&nbsp;" : c;
      })
      .join("");
  };

  const RenderIdentity = ({ identification }: RenderIdentity) => {
    return (
      <>
        <Row label="Rodzaj dokumentu">Dowód osobisty</Row>

        <Row label="Seria i numer">{identification?.seriesAndNumber}</Row>

        <Row label="Data wydania">{identification?.releaseDate}</Row>

        <Row label="Data ważności">
          {identification?.expirationDate ?? "Nieoznaczona"}
        </Row>

        <Row label="Kraj wydania">{identification?.issuingCountry.name}</Row>
      </>
    );
  };

  const RenderAddress = ({ address }: RenderAddress) => {
    return (
      <>
        <Row label="Kraj">
          <p className="wrapText" style={{ width: width / 3.5 }}>
            {address?.country?.name}
          </p>
        </Row>

        <Row label="Kod pocztowy">
          <p className="wrapText" style={{ width: width / 3.5 }}>
            {address.postCode}
          </p>
        </Row>

        <Row label="Miejscowość">
          <p className="wrapText" style={{ width: width / 3.5 }}>
            {address.city}
          </p>
        </Row>

        {address.street ? (
          <Row label="Ulica">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {address.street}
            </p>
          </Row>
        ) : null}

        <Row label="Numer domu">
          <p className="wrapText" style={{ width: width / 3.5 }}>
            {address.house}
          </p>
        </Row>

        {address.apartment ? (
          <Row label="Numer mieszkania">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {address.apartment}
            </p>
          </Row>
        ) : null}
      </>
    );
  };

  const RenderBenficiary = ({ beneficiary }: RenderBeneficiary) => {
    return (
      <>
        <Row label="Imię">
          <p className="wrapText" style={{ width: width / 3.5 }}>
            {beneficiary.firstName}
          </p>
        </Row>

        <Row label="Nazwisko">
          <p className="wrapText" style={{ width: width / 3.5 }}>
            {beneficiary.lastName}
          </p>
        </Row>

        {beneficiary.citizenship ? (
          <Row label="Kraj obywatelstwa">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.citizenship?.name}
            </p>
          </Row>
        ) : null}

        {beneficiary.pesel ? (
          <Row label="PESEL">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.pesel}
            </p>
          </Row>
        ) : null}

        {beneficiary.birthDate ? (
          <Row label="Data urodzenia">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.birthDate}
            </p>
          </Row>
        ) : null}

        {beneficiary.placeOfBirth ? (
          <Row label="Miejsce urodzenia">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.placeOfBirth}
            </p>
          </Row>
        ) : null}

        {beneficiary.countryOfBirth ? (
          <Row label="Kraj urodzenia">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.countryOfBirth.name}
            </p>
          </Row>
        ) : null}

        {beneficiary.documentType ? (
          <Row label="Rodzaj dokumentu">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.documentType.name}
            </p>
          </Row>
        ) : null}

        {beneficiary.seriesAndNumber ? (
          <Row label="Seria i numer">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.seriesAndNumber}
            </p>
          </Row>
        ) : null}

        {beneficiary.issuingCountry ? (
          <Row label="Kraj wydania">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.issuingCountry.name}
            </p>
          </Row>
        ) : null}

        {beneficiary.country ? (
          <Row label="Kraj zamieszkania">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.country.name}
            </p>
          </Row>
        ) : null}

        {beneficiary.postCode ? (
          <Row label="Kod pocztowy">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.postCode}
            </p>
          </Row>
        ) : null}

        {beneficiary.city ? (
          <Row label="Miejscowość">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.city}
            </p>
          </Row>
        ) : null}

        {beneficiary.street ? (
          <Row label="Ulica">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.street}
            </p>
          </Row>
        ) : null}

        {beneficiary.house ? (
          <Row label="Numer domu">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.house}
            </p>
          </Row>
        ) : null}

        {beneficiary.apartment ? (
          <Row label="Numer mieszkania">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              {beneficiary.apartment}
            </p>
          </Row>
        ) : null}

        {beneficiary.option === 1 ? (
          <Row label="PEP">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              Jestem osobą PEP: {beneficiary.pppText}
            </p>
          </Row>
        ) : null}

        {beneficiary.option === 2 ? (
          <Row label="PEP">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              Członek rodziny PEP: {beneficiary.pppText}
            </p>
          </Row>
        ) : null}

        {beneficiary.option === 3 ? (
          <Row label="PEP">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              Bliski współpracownik osoby PEP: {beneficiary.pppText}
            </p>
          </Row>
        ) : null}

        {beneficiary.option === 0 ? (
          <Row label="PEP">
            <p className="wrapText" style={{ width: width / 3.5 }}>
              nie jest osobą zajmującą eksponowane stanowisko polityczne,
              członkiem rodziny osoby zajmującej eksponowane stanowisko
              polityczne ani osobą znaną jako bliski współpracownik osoby
              zajmującej eksponowane stanowisko polityczne.
            </p>
          </Row>
        ) : null}
      </>
    );
  };

  return (
    <>
      <Table>
        <>
          <hr />
          <p className="bold red section">Dane osobowe</p>
          <Row label="Imię">{step1?.firstName}</Row>

          {step2?.secondName ? (
            <Row label="Drugie imię">{step2?.secondName}</Row>
          ) : null}

          <Row label="Nazwisko">{step1?.lastName}</Row>

          <Row label="Telefon">{step1?.phone}</Row>

          <Row label="Adres e-mail" disableUpperCase>
            {step1?.mail}
          </Row>

          <Row label="PESEL">{step2?.pesel ? step2?.pesel : "brak"}</Row>

          <Row label="Data urodzenia">{step2?.birthDate}</Row>

          <Row label="Płeć">{step2?.gender?.name}</Row>

          <Row label="Miejsce urodzenia">{step2?.placeOfBirth}</Row>

          <Row label="Kraj urodzenia">{step2?.countryOfBirth?.name}</Row>

          <Row label="Kraj obywatelstwa">{step2?.citizenship?.name}</Row>

          <Row label="Nazwisko panieńskie matki">
            {step2?.mothersMaidenName}
          </Row>

          {step2?.address.motherName ? (
            <Row label="Imię matki">{step2?.address.motherName}</Row>
          ) : null}
          {step2?.address.fatherName ? (
            <Row label="Imię ojca">{step2?.address.fatherName}</Row>
          ) : null}

          <Row label="Zawód">{step2?.profession?.name}</Row>

          {step2?.identification ? (
            <>
              <hr className="hrMargin" />

              <p className="bold red section">Dowód osobisty</p>
              <RenderIdentity identification={step2?.identification} />
            </>
          ) : null}

          {step2?.address ? (
            <>
              <hr className="hrMargin" />
              <p className="bold red section">Adres zamieszkania</p>
              <RenderAddress address={step2?.address} />
            </>
          ) : null}

          {step2?.postalAddress ? (
            <>
              <hr className="hrMargin" />

              <p className="bold red section">Adres korespondencyjny</p>
              <RenderAddress address={step2?.postalAddress} />
            </>
          ) : null}

          <hr className="hrMargin" />

          <p className="bold red section">
            Ankieta Przeciwdziałania Praniu Pieniędzy:
          </p>

          {ppp?.survey ? (
            <>
              {ppp?.survey.questions
                .filter(
                  (question) => showQuestion.indexOf(question.type) !== -1
                )
                .map((question) => {
                  return (
                    <Row label={`${question.text}`} breakWord>
                      <>
                        {question?.answers
                          ?.filter((answer) => answer.isSelected)
                          .map((item) => {
                            if (item.showTextBox) {
                              return (
                                <p
                                  className="wrapText"
                                  style={{ width: width / 3.5 }}
                                >
                                  {item.answerText} {item.additionalText}
                                </p>
                              );
                            } else {
                              return (
                                <p
                                  className="wrapText"
                                  style={{ width: width / 3.5 }}
                                >
                                  {item.answerText}
                                </p>
                              );
                            }
                          })}
                      </>
                    </Row>
                  );
                })}
            </>
          ) : null}

          {ppp?.surveyHighRisk != null &&
          ppp?.surveyHighRisk?.questions.filter((f) =>
            f.answers?.find((answer) => answer.isSelected === true)
          )?.length > 0 ? (
            <>
              {ppp?.surveyHighRisk.questions
                .filter(
                  (question) => showQuestion.indexOf(question.type) !== -1
                )
                .map((question) => {
                  return (
                    <Row label={`${question.text}`} breakWord>
                      <>
                        {question?.answers
                          ?.filter((answer) => answer.isSelected)
                          .map((item) => {
                            if (
                              item.showTextBox &&
                              isEmpty(item.answerText) === false
                            ) {
                              return (
                                <p
                                  className="wrapText"
                                  style={{ width: width / 3.5 }}
                                >
                                  {item.answerText} {item.additionalText}
                                </p>
                              );
                            } else {
                              if (isEmpty(item.answerText) === false)
                                return (
                                  <p
                                    className="wrapText"
                                    style={{ width: width / 3.5 }}
                                  >
                                    {item.answerText}
                                  </p>
                                );
                            }
                          })}
                      </>
                    </Row>
                  );
                })}
            </>
          ) : null}

          {ppp?.withBeneficiary == true && ppp?.firstBeneficiary ? (
            <>
              <hr className="hrMargin" />
              <p className="bold red section">Beneficjent rzeczywisty</p>

              <RenderBenficiary beneficiary={ppp?.firstBeneficiary} />
            </>
          ) : null}

          {ppp?.withBeneficiary == true && ppp?.secondBeneficiary ? (
            <>
              <hr className="hrMargin" />
              <p className="bold red section">Beneficjent rzeczywisty 2</p>

              <RenderBenficiary beneficiary={ppp?.secondBeneficiary} />
            </>
          ) : null}

          {ppp?.withBeneficiary == true && ppp?.thirdBeneficiary ? (
            <>
              <hr className="hrMargin" />
              <p className="bold red section">Beneficjent rzeczywisty 3</p>

              <RenderBenficiary beneficiary={ppp?.thirdBeneficiary} />
            </>
          ) : null}

          <hr className="hrMargin" />
          <p className="bold red section">Załączniki</p>
        </>
      </Table>
      <div className="row px-3 mt-1 mb-1">
        {step2?.identificationPhotos.mainSide ? (
          <>
            <div className={`${width < 400 ? "col-12" : "col-4"}`}>
              <div
                className="attachmentsTile"
                onClick={() =>
                  downloadPDF(
                    step2?.identificationPhotos.mainSide,
                    "Dokument_tozsamości_nr_1_strona_ze_zdjeciem"
                  )
                }
              >
                <SystemUpdateAltIcon
                  style={{ fill: "#ac906b" }}
                  fontSize="large"
                />
                <span style={{ textAlign: "center" }}>
                  Dowód osobisty - strona ze zdjęciem
                </span>
              </div>
            </div>
          </>
        ) : null}
        {step2?.identificationPhotos.backSide ? (
          <>
            <div className={`${width < 400 ? "col-12" : "col-4"}`}>
              <div
                className="attachmentsTile"
                onClick={() =>
                  downloadPDF(
                    step2?.identificationPhotos.backSide,
                    "Dokument_tozsamości_nr_1_druga_strona"
                  )
                }
              >
                <SystemUpdateAltIcon
                  style={{ fill: "#ac906b" }}
                  fontSize="large"
                />
                <span style={{ textAlign: "center" }}>
                  Dowód osobisty - druga strona
                </span>
              </div>
            </div>
          </>
        ) : null}
      </div>
      <br />
      <div className="row px-3 mt-1 mb-1">
        <div
          onClick={() => setDataCheck((prev) => !prev)}
          className="custom-control custom-checkbox"
        >
          <input
            id="dataConfirm"
            type="checkbox"
            className="custom-control-input"
            checked={dataCheck}
          />
          <label className="custom-control-label">
            Oświadczam, że zapoznałam się/zapoznałem się z Wzorem Umowy Dostępu
            do Platformy Xelion oraz Regulaminem Dostępu do Platformy Xelion.
          </label>
        </div>
      </div>
      <div></div>

      <form onSubmit={handleSubmit(confirmPhone)}>
        <div
          className={`${
            state.value === "confirmPhone"
              ? "confirmEmailShow"
              : "confirmEmailHide"
          }`}
        >
          {state.value === "confirmPhone" ? (
            <ThemeProvider theme={defaultMaterialTheme}>
              <div className="row px-3 mt-3">
                <div className="code" style={{ width: "160px" }}>
                  <div className="form-group mt-1 mb-1">
                    <Controller
                      render={({ field }) => (
                        <TextFieldStyled
                          label="Kod weryfikacyjny"
                          variant="outlined"
                          value={field.value}
                          error={errors.code != null || errorCode}
                          onChange={(event: any) => {
                            field.onChange(event.target.value);
                            setErrorCode(false);
                          }}
                          inputProps={{
                            maxLength: 6,
                          }}
                        />
                      )}
                      name="code"
                      control={control}
                      rules={{
                        required: "Kod weryfikacyjny jest wymagany",
                      }}
                    />
                  </div>
                </div>
                <div className="col-2 codeBtn">
                  <div className=" mt-3 mb-1 marginButton">
                    <input className="nextBtn" type="submit" value="Dalej" />
                  </div>
                </div>
                <div className="col-12 code">
                  {errors.code && (
                    <p className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      {errors.code.message}
                    </p>
                  )}
                  {errorCode && (
                    <p className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      Podano błędny kod
                    </p>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-3 mb-1">
                Aby kontynuować należy w polu powyżej wpisać kod weryfikacyjny
                nr {counter} wysłany w wiadomości SMS na podany numer telefonu.
                Jeśli kod nie dotarł należy kliknąć przycisk „Pobierz ponownie
                kod".
              </div>
            </ThemeProvider>
          ) : null}
        </div>

        {showError && loading === false && (
          <span className="validationError" role="alert">
            <i className="fa fa-exclamation-circle" />
            Zaznaczenie oświadczenia jest wymagane.
          </span>
        )}

        <div className="row px-3 mb-3 mt-3">
          <div className="col-sm-12 code">
            <div className="mr-12  float-right">
              {state.value === "confirmPhone" ? (
                <>
                  {width <= 321 ? (
                    <>
                      <button
                        onClick={(event) => {
                          event.preventDefault();
                          clearErrors("code");
                          setStep(4);
                          send({ type: "TOGGLE" });
                        }}
                        className="backBtn"
                      >
                        Cofnij
                      </button>
                      <span onClick={sendCode} className="sendBtn">
                        Pobierz kod
                      </span>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={(event) => {
                          event.preventDefault();
                          clearErrors("code");
                          setStep(4);
                          send({ type: "TOGGLE" });
                        }}
                        className="backBtn"
                      >
                        Cofnij
                      </button>
                      <span onClick={sendCode} className="sendBtn">
                        Pobierz ponownie kod
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={async () => {
                      await clearPPP();
                      setStep(7);
                    }}
                    className="backBtn"
                  >
                    Cofnij
                  </button>
                  <input className="nextBtn" type="submit" value="Dalej" />
                </>
              )}
            </div>
          </div>
        </div>
      </form>
      {loading && <LoadingPanel />}
    </>
  );
};

export { Step3Form };
